import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@mui/material/Typography';
import { Box, Fade, FormControlLabel, Stack, Switch } from '@mui/material';
import { useLocationStore } from '@libs/store/locations';
import { useShiftsFilter } from '@libs/store/shifts';
import { ActiveFilters, FiltersDisplayCollection, StartDateRangeFilterInputControlled } from '@molecules/filters';
import { FilterAutocomplete, FilterMultiSelect, SearchField, renderCheckboxOption } from '@atoms/inputs';
import { Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { ShiftsFilterMenu } from '../filterMenu';
import { useOrgQualificationsStore } from '@libs/store/settings';
import { useEffect, useState } from 'react';
import { TIMEZONE_FALLBACK } from '@libs/config';
import dayjs, { Dayjs } from 'dayjs';
import { DateRangeFilter, isDateRangeFilter } from '@libs/store/filters';

type ShiftsFiltersProps = {
  loading?: boolean;
};

const FallbackTimezone = TIMEZONE_FALLBACK;

export function ShiftsFilters({ loading: upperLoading }: ShiftsFiltersProps) {
  const intl = useIntl();
  const {
    activeFilters,
    clearFilter,
    clearFilters,
    getFilter,
    setFilter,
    setFilterFromOptions,
    filterProviders,
    loadData,
    loadProvidersFiltersData,
  } = useShiftsFilter();
  const { loading: qualListLoading, orgQualifications, load: loadQualifications } = useOrgQualificationsStore();
  const { current: currentLocation, currentLocationID, loading: locationsLoading } = useLocationStore();

  const startDateFilter = activeFilters.startDate as DateRangeFilter | undefined;
  const startDate =
    startDateFilter && isDateRangeFilter(startDateFilter)
      ? { from: dayjs(startDateFilter.value.from), to: dayjs(startDateFilter.value.to) }
      : undefined;
  const dateLabel = [startDate?.from.format('MMM D, YYYY'), startDate?.to.format('MMM D, YYYY')]
    .filter(Boolean)
    .join(' - ');

  const loading = upperLoading || locationsLoading || qualListLoading;

  const professionalsOptions = filterProviders.map((talent) => ({
    label: [talent.firstName, talent.lastName].filter(Boolean).join(' '),
    value: talent.id,
    qualifications: talent.qualifications.map((q) => q.abbreviation).join(', '),
  }));

  const [providersSearchTerm, setProvidersSearchTerm] = useState<string>('');

  const searchValue = getFilter<string>('search')?.value ?? '';

  useEffect(() => {
    if (currentLocationID) {
      loadData({
        locationId: currentLocationID,
      });
    }
  }, [loadData, currentLocationID]);

  useEffect(() => {
    if (currentLocationID && providersSearchTerm) {
      loadProvidersFiltersData({ locationId: currentLocationID, searchTerm: providersSearchTerm });
    }
  }, [loadProvidersFiltersData, currentLocationID, providersSearchTerm]);

  useEffect(() => {
    if (!orgQualifications) {
      loadQualifications();
    }
  }, [loadQualifications, orgQualifications]);

  const qualificationsFilterOptions =
    orgQualifications?.direct.map((qual) => ({
      label: `${qual.abbreviation ? qual.abbreviation : qual.name}`,
      value: qual.id,
    })) || [];

  const handleClearFilters = () => {
    if (currentLocationID) {
      clearFilters();
      setProvidersSearchTerm('');
      setFilter({ key: 'locations', value: [currentLocationID] });
    }
  };

  const filtersDisplayCollection: FiltersDisplayCollection = Object.keys(
    activeFilters,
  ).reduce<FiltersDisplayCollection>((acc, filterKey) => {
    const dayFilterAttempt = activeFilters[filterKey].value as { from: Dayjs; to: Dayjs; timezone: string };
    if (filterKey === 'startDate' && dayFilterAttempt && dayFilterAttempt.from && dayFilterAttempt.to) {
      acc = {
        ...acc,
        [filterKey]: {
          ...activeFilters[filterKey],
          valueView: `${dayjs(dayFilterAttempt.from).format('MMM D, YYYY')} - ${dayjs(dayFilterAttempt.to).format('MMM D, YYYY')}`,
          label: <FormattedMessage id="shifts.fields.startDate" />,
        },
      };
      return acc;
    }

    if (!['locations', 'startDate', 'hasAssignee'].includes(filterKey)) {
      acc = {
        ...acc,
        [filterKey]: {
          ...activeFilters[filterKey],
          label: <FormattedMessage id={`shifts.fields.${filterKey}`} />,
        },
      };
    }

    return acc;
  }, {});

  const shouldShowClearAllButton = Object.keys(filtersDisplayCollection).length > 0;

  return (
    <>
      <Stack direction="row" spacing={2}>
        <Stack direction="row" spacing={2}>
          <Box width="220px">
            <StartDateRangeFilterInputControlled
              dateLabel={dateLabel}
              dateValue={
                startDateFilter && isDateRangeFilter(startDateFilter)
                  ? { from: dayjs(startDateFilter.value.from), to: dayjs(startDateFilter.value.to) }
                  : undefined
              }
              timezone={currentLocation?.timezone || FallbackTimezone}
              loading={loading}
              onClear={() => clearFilter('startDate')}
              onSetFilter={(filter) =>
                setFilter({
                  ...filter,
                  key: 'startDate',
                })
              }
            />
          </Box>
          <Box width={220}>
            <FilterMultiSelect
              size="small"
              disabled={loading}
              options={qualificationsFilterOptions}
              inlineLabel
              label={`${intl.formatMessage({ id: 'common.qualification' })}:`}
              onApply={(v) => setFilterFromOptions('qualifications', v, qualificationsFilterOptions)}
              onClear={() => clearFilter('qualifications')}
              value={getFilter<string[]>('qualifications')?.value ?? []}
              fullWidth
              renderOption={renderCheckboxOption}
            />
          </Box>
          <Box width={220}>
            <FilterAutocomplete
              options={professionalsOptions}
              loading={loading}
              disabled={loading}
              label={intl.formatMessage({ id: 'fields.talents' })}
              fullWidth
              async
              onClear={() => {
                clearFilter('staff');
                setProvidersSearchTerm(''); // Clear search term when filter is cleared
              }}
              onSearchTermChange={(v) => setProvidersSearchTerm(v)}
              onApply={(v) => {
                setFilterFromOptions('staff', v, professionalsOptions);
              }}
              value={getFilter<string[]>('staff')?.value ?? []}
              renderLabel={(option) => (
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Typography noWrap>{option.label}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                  >
                    {professionalsOptions.find((o) => o.value === option.value)?.qualifications}
                  </Typography>
                </Stack>
              )}
            />
          </Box>
          <Box width={220}>
            <FilterMultiSelect
              size="small"
              options={statusesOptions}
              disabled={loading}
              inlineLabel
              label={`${intl.formatMessage({ id: 'fields.statuses' })}:`}
              fullWidth
              renderOption={renderCheckboxOption}
              onApply={(v) => setFilterFromOptions('statuses', v, statusesOptions)}
              onClear={() => clearFilter('statuses')}
              value={getFilter<string[]>('statuses')?.value ?? []}
            />
          </Box>
          <ShiftsFilterMenu loading={loading} timezone={currentLocation?.timezone} />
          <FormControlLabel
            control={
              <Switch
                defaultChecked={getFilter('hasAssignee')?.value === 'true'}
                onChange={(event, value) => {
                  if (value) {
                    setFilter({
                      key: 'hasAssignee',
                      value: value ? 'true' : '',
                    });
                  } else {
                    clearFilter('hasAssignee');
                  }
                }}
              />
            }
            label="Show scheduled"
          />
        </Stack>
        <Box flex={1} />
        <SearchField
          value={searchValue ?? ''}
          sx={{ width: 260, flexShrink: 0 }}
          onDebouncedChange={(v) => (v ? setFilter({ key: 'search', value: v }) : clearFilter('search'))}
        />
      </Stack>
      {shouldShowClearAllButton && (
        <Stack direction="row" mt={1} spacing={1} flexWrap="wrap" alignItems="center">
          <ActiveFilters filters={filtersDisplayCollection} onDelete={(item) => clearFilter(item.key)} />
          <Box flex={1} />
          <Fade in={shouldShowClearAllButton} timeout={150}>
            <Button
              variant="passive"
              onClick={() => handleClearFilters()}
              startIcon={<FaIcon name="xmark" />}
              sx={{ ml: 'auto' }}
            >
              <FormattedMessage id="common.clearAllFilters" />
            </Button>
          </Fade>
        </Stack>
      )}
    </>
  );
}

const statusesOptions = [
  { label: 'Open', value: 'Opened' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Assigned', value: 'Assigned' },
  { label: 'Re-assigned', value: 'Reassigned' },

  { label: 'Canceled', value: 'Canceled' },
  { label: 'In progress', value: 'InProgress' },
  { label: 'Unfilled', value: 'Unfilled' },
  // { label: 'Deleted', value: 'Deleted' },
  { label: 'Expired', value: 'Expired' },
  { label: 'Unassigned', value: 'Unassigned' },
  { label: 'Completed', value: 'Completed' },
];
